import { Link } from "@tanstack/react-router";
import clsx from "clsx";
import { COMPONENT_STATE } from "src/models/constants";
import { Icon } from "../icon/Icon";
import styling from "./Button.module.scss";
import { Flex } from "@scaleaq/sui-react";
import type { ComponentProps } from "react";
import type { ColorTokenRole, ColorTokenVariant, ComponentStateType, SizeType } from "@scaleaq/sui-react/models/types";

export namespace Button {
	export type ElementName = "button" | "a";
	export type ElementType = HTMLElementTagNameMap[ElementName];
	export type PaddingRule =
		| "text-only"
		| "icon-only"
		| "icon-and-text"
		| "icon-left-and-text"
		| "icon-right-and-text";
	export type Props = Omit<ComponentProps<"button">, "color" | "content"> & {
		size?: Extract<SizeType, "xsmall" | "small" | "medium" | "large">;
		color?: Extract<
			ColorTokenRole,
			| "action"
			| "neutral"
			| "success"
			| "caution"
			| "critical"
			| "warning"
			| "alarm"
		>;
		variant?: Extract<
			ColorTokenVariant,
			"solid" | "soft" | "bordered" | "ghost"
		>;
		paddingRule?: PaddingRule;
		state?: ComponentStateType;
		href?: string;
		text?: string;
		leftIcon?: Icon.Props | React.ReactNode;
		rightIcon?: Icon.Props | React.ReactNode;
		radiusType?: "rounded" | "pill" | "curved" | "square";
		alignment?: Extract<Flex.Root.Props["alignment"], "center-left" | "center-right" | "center">;
	};
}

export const Button = (props: Button.Props) => {
	// *****************************************
	// #region Properties
	// *****************************************

	const {
		radiusType = "curved",
		alignment = "center-left",
		color = "action",
		variant = "solid",
		state = "default",
		size = "medium",
		paddingRule = "text-only",
		text,
		leftIcon,
		rightIcon,
		children,
		href,
		className,
		onClick,
		ref,
		...rest
	} = props;

	// *****************************************
	// #endregion
	// *****************************************

	// *****************************************
	// #region Handling
	// *****************************************

	const _isLoading = () => {
		return state === COMPONENT_STATE.loading;
	};

	const _isDisabled = () => {
		return state === COMPONENT_STATE.disabled || _isLoading();
	};

	const _isActive = () => {
		return state === COMPONENT_STATE.active && !_isDisabled();
	};

	const _getSizeClasses = () => {
		let _temp: Button.PaddingRule = paddingRule;
		const hasChildren: boolean = !!(children || text?.length);

		if (leftIcon && rightIcon && hasChildren) {
			_temp = "icon-and-text";
		} else if (leftIcon && !rightIcon && hasChildren) {
			_temp = "icon-left-and-text";
		} else if (rightIcon && !leftIcon && hasChildren) {
			_temp = "icon-right-and-text";
		} else if ((leftIcon || rightIcon) && hasChildren) {
			_temp = "icon-only";
		}

		return clsx(styling.size, {
			[styling[`size__${size}`]]: size,
			[styling[`size__${size}--${_temp}`]]: _temp,
		});
	};

	const _getClasses = () => {
		return clsx(
			styling.button,
			{
				[styling[`color-${color}`]]: !!color,
				[styling[`color-${color}--${variant}`]]: !_isDisabled(),
				[styling[`color-${color}--${variant}--active`]]: _isActive(),
				[styling[`disabled--${variant}`]]: _isDisabled(),
				[styling[`radius--${radiusType}`]]: radiusType,
				[styling[`alignment--${alignment}`]]: alignment,
				[`${className}`]: className,
			},
			_getSizeClasses(),
		);
	};

	// *****************************************
	// #endregion
	// *****************************************

	// *****************************************
	// #region Render
	// *****************************************

	const _renderLeftIcon = () => {
		if (leftIcon) {
			if (typeof leftIcon === "object" && typeof (leftIcon as Icon.Props).name === "string") {
				return <Icon {...(leftIcon as Icon.Props)} />;
			} else {
				return <>{leftIcon}</>;
			}
		}
	};

	const _renderRightIcon = () => {
		if (rightIcon) {
			if (typeof rightIcon === "object" && typeof (rightIcon as Icon.Props).name === "string") {
				return (
					<div className={styling["right-icon-wrapper"]}>{<Icon {...(rightIcon as Icon.Props)} />}</div>
				);
			} else {
				return <div className={styling["right-icon-wrapper"]}>{rightIcon as any}</div>;
			}
		}
	};

	const _renderContent = () => {
		return (
			<>
				{_renderLeftIcon()}
				{text}
				{children}
				{_renderRightIcon()}
			</>
		);
	};

	if (href) {
		return (
			<Link
				onClick={() => {
					if (onClick) {
						onClick({} as any);
					}
				}}
				to={href}
				data-loading={_isLoading()}
				data-active={_isActive()}
				data-disabled={_isDisabled()}
				className={_getClasses()}
				ref={ref as any}
			>
				{_renderContent()}
			</Link>
		);
	}

	return (
		<button
			data-loading={_isLoading()}
			data-active={_isActive()}
			data-disabled={_isDisabled()}
			disabled={_isDisabled()}
			onClick={_isDisabled() ? undefined : onClick}
			className={_getClasses()}
			ref={ref}
			{...rest}
		>
			{_renderContent()}
		</button>
	);

	// *****************************************
	// #endregion
	// *****************************************
};
